<template>
  <div class="schedule-product-tile" data-cy="schedule-product-tile">
    <div class="tile-check__wrapper">
      <input
        v-if="canModifyCarts"
        type="checkbox"
        name="tile-checkbox"
        v-model="model.selected"
      />
      <label
        v-if="canModifyCarts"
        class="tile-check"
        for="tile-checkbox"
        @click="model.selected = !model.selected"
      ></label>
    </div>
    <header class="product-item__header">
      <span class="product-name">
        <div @click="onTileClick">
          <span class="schedule-product-tile__title" data-cy="schedule-product-tile-title">
            {{ model.LabelName }}
            {{ model.LabelNumber ? "/ " + model.LabelNumber : "" }}
          </span>
          <span class="label-dpm" data-cy="schedule-product-tile-dpm">[{{ dpm }}]</span>
        </div>
        <span class="task-list__card-info task-list__card-info--label">
          <div @click="onTileClick">
            <div data-cy="schedule-product-tile-company">{{ model.Company }}</div>
            <div data-cy="schedule-product-tile-brand">{{ model.BrandName }}</div>
            <div data-cy="schedule-product-tile-agency">
              {{ model.VueFullUkdIdWithSuppressUkdId }}
            </div>
            <div data-cy="schedule-product-tile-plantname">
              Plant: {{ model.PlantName }}
            </div>
            <div class="special-req-container">
              <span
                v-if="model._Pesach"
                class="special-req-indicator"
                data-cy="schedule-product-tile-passover"
                >Passover</span
              >
              <span
                v-if="model.SpecialRequirementsExists"
                class="special-req-indicator"
                data-cy="schedule-product-tile-comments"
                >Special Requirements</span
              >
            </div>
          </div>
          <div class="loc-download-container">
            <a
              v-if="model.IsOkToPrintLoc"
              class="loc-dwnld"
              :href="`UkdSingleProductLoc?UKD_id=${model.VueFullUkdIdWithSuppressUkdId}`"
              data-cy="schedule-product-tile-loc"
            >
              <DownloadIcon />
              Product LOC
            </a>
          </div>
        </span>
      </span>
      <span class="status" :class="productStatus">
        {{ productStatus }}
      </span>
    </header>

    <div v-show="showDetails" class="product-details" data-cy="product-details">
      <Grid
        :data-items="productProperties"
        :columns="columns"
        :scrollable="'none'"
      >
        <template v-slot:emptyHeader="{}"> </template>
      </Grid>
    </div>
  </div>
</template>

<script setup lang="ts">
import { EnumDpm } from "@/Domain/EnumDpm";
import { computed, ref, watchEffect } from "vue";
import DownloadIcon from "@/icons/DownloadIcon.vue";
import { EnumLabelType } from "@/Domain/EnumLabelType";
import { EnumMerchStatus } from "@/Domain/EnumMerchStatus";
import { convertNumber } from "@/Domain/EnumGroupConverter";
import type { ProductWithSelection } from "@/api/kosherCertificates";
import { Grid, type GridColumnProps } from "@progress/kendo-vue-grid";

defineProps<{
  canModifyCarts: boolean;
}>();

const model = defineModel<ProductWithSelection>({ required: true });
const showDetails = ref<boolean>(false);
const productProperties = ref<{ name: string; value: string }[]>([]);

const productStatus = computed(() => {
  return EnumMerchStatus[model.value._Status!];
});

const dpm = computed(() => {
  return EnumDpm[model.value._Dpm!];
});

const group = computed(() => {
  return convertNumber(model.value._Group!);
});

const labelType = computed(() => {
  return EnumLabelType[model.value._LabelType!];
});

const merchStatus = computed(() => {
  return EnumMerchStatus[model.value._MerchStatus!];
});

const columns: GridColumnProps[] = [
  {
    field: "name",
    headerCell: "emptyHeader",
    className: "name-row",
  },
  {
    field: "value",
    headerCell: "emptyHeader",
  },
];

const setProductProperties = function () {
  const rows: any[] = [];

  rows.push(
    {
      name: "Group",
      value: group.value,
    },
    {
      name: "Symbol",
      value: model.value.Symbol,
    },
    {
      name: "Label Type",
      value: labelType.value,
    },
    {
      name: "Product Status",
      value: merchStatus.value,
    }
  );

  if (model.value.MerchAndLabelCommentsPublic?.length)
    rows.push({
      name: "Special Requirements",
      value: model.value.MerchAndLabelCommentsPublic,
    });

  if (model.value.MerchLabelScheduleBComment?.replace(/\s/g, "")?.length)
    rows.push({
      name: "Schedule B Comments",
      value: model.value.MerchLabelScheduleBComment,
    });

    if (model.value.SealSignComment?.replace(/\s/g, "")?.length)
    rows.push({
      name: "Seal or Signature Required",
      value: model.value.SealSignComment,
    });  

  rows.push({
    name: "Consumer / Industrial",
    value: model.value._Consumer ? "C" : "I",
  });

  productProperties.value = rows;
};

setProductProperties();

const onTileClick = function (e: MouseEvent) {
  const el = e.target as Element;
  if (el && el.tagName.toLowerCase() === "a") return;
  showDetails.value = !showDetails.value;
};

watchEffect(() => {
  setProductProperties();
});
</script>

<style lang="scss">
.schedule-product-tile {
  background: #fff;
  margin-bottom: 10px;
  border-radius: 4px;
  padding: 18px 15px 15px 65px;
  cursor: pointer;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  position: relative;

  &.__title {
    font: 16px/1.5 proxima-nova, sans-serif;
    color: #444955;
    background-color: #fff;
    font-weight: 400;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
  }

  .label-dpm {
    display: inline-block;
    font-size: 12px;
    margin-left: 5px;
    color: #999;
  }

  .task-list__card-info--label {
    color: #a5a5a5;
    font-size: 12px;
    margin-top: 6px;
    display: block;

    .loc-download-container {
      display: flex;
      gap: 12px;
      margin-top: 12px;
    }

    .loc-dwnld {
      display: inline-flex;
      color: white;
      background: #33669a;
      padding: 5px 15px;
      align-items: center;
      text-decoration: none;

      svg {
        margin-right: 5px;
        fill: white;
      }
    }
  }

  .special-req-container {
    display: flex;
    gap: 12px;
  }

  .special-req-indicator {
    display: inline-block;
    margin-top: 5px;
    background: #666;
    color: white;
    font-size: 11px;
    padding: 3px 7px;
  }

  .product-details {
    margin-top: 12px;

    .k-grid {
      border: 0;
    }

    .k-grid-header {
      display: none !important;
    }
  }

  .tile-check__wrapper {
    input {
      position: absolute;
      left: -999px;
    }
    label.tile-check {
      width: 15%;
      max-width: 60px;
      height: 100%;
      background: #f7f7f7;
      border: 1px solid #e9edef;
      border-right: 1px solid #efefef;
      border-radius: 4px 0 0 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 10px;
      position: absolute;
      left: 0;
      top: 0;
    }

    input:checked ~ label {
      background: #428bca;
      border-color: #428bca;
    }

    input:checked ~ label:before {
      content: "✓";
      color: white;
      font-size: 20px;
    }
  }
}

.status {
  position: absolute;
  top: 0;
  right: 0;
  border-radius: 0 6px 0 6px;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: bold;
  background: #fff1eb;
  color: #c24100;
  margin-bottom: 10px;
  &.certified {
    background: #eaf9de;
    color: #008a00;
  }
}
</style>
